import { ControlType } from './constant/template'

const getNumericScore = value => {
    if (value === '' || value === null || value === undefined) return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

export const getQuestionScoreFallback = question => {
    if (!question) return 0
    if (typeof question.Score === 'number' && question.Score > 0) {
        return question.Score
    }
    if (typeof question.MaxScore === 'number' && question.MaxScore > 0) {
        return question.MaxScore
    }
    return 0
}

/** Whether a single-response question has a user answer that should count toward score. */
export const isSingleResponseAnsweredForScoring = question => {
    if (!question || Number(question.TypeOfResponse) !== 1) {
        return false
    }

    const response = question.Answers?.SingleResponse?.Response
    if (response === undefined || response === null || response === '') {
        return false
    }

    const rtId = Number(question.QuestionResponseId)
    if (rtId === ControlType.Checkbox) {
        return response === 'true'
    }
    if (rtId === ControlType.AutoNumber) {
        return false
    }
    if (rtId === ControlType.Number || rtId === ControlType.Slider) {
        const n = Number(response)
        return !Number.isNaN(n) && n > 0
    }

    return String(response).trim() !== ''
}

export const isMediaQuestionAnsweredForScoring = question =>
    Number(question?.QuestionResponseId) === ControlType.Media &&
    Array.isArray(question?.Files) &&
    question.Files.length > 0

/** Per-question score shown in the UI after the user answers. */
export const getQuestionDisplayScore = question => {
    if (!question) return 0

    const answers = question.Answers || {}
    const questionScore = getQuestionScoreFallback(question)
    const pcr = answers.ParentChildResponse
    const hasParentChild = Array.isArray(pcr) && pcr.length > 0

    if (hasParentChild) {
        if (!hasParentChildAnswerSelection(pcr, answers)) {
            return 0
        }
        const displayScore = getParentChildQuestionScore(
            pcr,
            questionScore,
            answers.OtherAnswerResponse,
        )
        return displayScore > 0 ? displayScore : 0
    }

    if (Number(question.QuestionResponseId) === ControlType.Media) {
        if (!isMediaQuestionAnsweredForScoring(question)) {
            return 0
        }
        return questionScore > 0 ? questionScore : 0
    }

    if (
        Number(question.TypeOfResponse) === 2 ||
        Number(question.MultiResponseType) === 2 ||
        Number(question.MultiResponseType) === 1
    ) {
        const responses = answers.MultipleResponse || []
        const selectedResponses = responses.filter(res => res.IsSelected)
        if (!selectedResponses.length) {
            return 0
        }

        // Selected option Score 0 is valid; do not fall back to question/MaxScore.
        const optionScores = selectedResponses.map(res =>
            getNumericScore(res.Score),
        )
        const hasExplicitScore = optionScores.some(score => score !== null)
        if (hasExplicitScore) {
            const responseScore = optionScores.reduce(
                (sum, score) => sum + (score || 0),
                0,
            )
            return responseScore > 0 ? responseScore : 0
        }

        return questionScore > 0 ? questionScore : 0
    }

    if (!isSingleResponseAnsweredForScoring(question)) {
        return 0
    }

    return questionScore > 0 ? questionScore : 0
}

/** Sum scored points across all sections (matches UI total and per-question badges). */
export const computeInspectionTotalScore = auditData => {
    if (!auditData?.Sections?.length) {
        return 0
    }

    let total = 0
    auditData.Sections.forEach(section => {
        if (!Array.isArray(section.Questions)) {
            return
        }
        section.Questions.forEach(question => {
            total += getQuestionDisplayScore(question)
        })
    })
    return total
}

export const applyInspectionScoreTotals = auditData => {
    if (!auditData) {
        return auditData
    }
    return {
        ...auditData,
        TotalScore: computeInspectionTotalScore(auditData),
    }
}

export const getSelectedParentFromPcr = parentChildResponse => {
    if (!Array.isArray(parentChildResponse)) return null
    return (
        parentChildResponse.find(
            parent =>
                !!parent?.IsSelected ||
                (Array.isArray(parent?.Children) &&
                    parent.Children.some(child => !!child?.IsSelected)),
        ) || null
    )
}

export const getSelectedChildren = parent => {
    if (!parent || !Array.isArray(parent.Children)) return []
    return parent.Children.filter(child => !!child?.IsSelected)
}

export const getLastSelectedChild = parent => {
    if (!parent || !Array.isArray(parent.Children)) return null
    let last = null
    parent.Children.forEach(child => {
        if (child?.IsSelected) last = child
    })
    return last
}

export const hasParentChildAnswerSelection = (
    parentChildResponse,
    answers = null,
) => {
    if (!Array.isArray(parentChildResponse) || parentChildResponse.length === 0) {
        return false
    }

    const selectedParent = getSelectedParentFromPcr(parentChildResponse)
    if (!selectedParent) {
        return false
    }

    const children = Array.isArray(selectedParent.Children)
        ? selectedParent.Children
        : []

    // Parent with no sub-options (e.g. Conforming): selecting the parent is enough.
    if (children.length === 0) {
        return !!selectedParent.IsSelected
    }

    if (getSelectedChildren(selectedParent).length > 0) {
        return true
    }

    if (
        selectedParent.IsOtherOptionAvailable &&
        answers?.IsOtherAnswerSelected &&
        Array.isArray(answers?.OtherAnswerResponse)
    ) {
        const uniqueId = selectedParent.Id ?? selectedParent.ParentId
        const hasFilledOther = answers.OtherAnswerResponse.some(other => {
            if (!other) return false
            const matchesParent =
                String(other?.UniqueId) === String(uniqueId) ||
                String(other?.ParentId) === String(selectedParent.ParentId)
            if (!matchesParent) return false
            const value =
                typeof other.OtherAnswer === 'string'
                    ? other.OtherAnswer.trim()
                    : other.OtherAnswer
            return value !== undefined && value !== null && value !== ''
        })
        if (hasFilledOther) {
            return true
        }
    }

    // NC parent selected but no child / other chosen yet.
    return false
}

export const resolveChildIsScore = child => {
    const scoreNum = Number(
        child?.Score ?? child?.ChildScore ?? child?.OriginalScore ?? 0,
    )
    if (!Number.isNaN(scoreNum) && scoreNum > 0) return true
    if (child?.IsScore === true || child?.IsChildScore === true) return true
    if (child?.IsScore === false || child?.IsChildScore === false) return false
    return false
}

export const parseScoreFromLabel = label => {
    const nameMatch = String(label || '').match(/\((\d+(?:\.\d+)?)\)\s*$/)
    if (!nameMatch) return null
    const parsed = Number(nameMatch[1])
    return Number.isNaN(parsed) ? null : parsed
}

export const getChildScoreValue = child => {
    const scoreNum = Number(child?.Score ?? child?.ChildScore ?? 0)
    return Number.isNaN(scoreNum) ? 0 : scoreNum
}

export const resolveParentDefaultScore = parent => {
    if (!parent) return 0

    const original = Number(parent.OriginalScore)
    if (!Number.isNaN(original) && original > 0) return original

    const current = Number(parent.Score)
    if (!Number.isNaN(current) && current > 0) return current

    const fromName = parseScoreFromLabel(parent.ParentName)
    if (fromName !== null && fromName > 0) return fromName

    return 0
}

export const resolveChildDefaultScore = child => {
    if (!child) return 0

    const original = Number(child.OriginalScore)
    if (!Number.isNaN(original) && original >= 0) return original

    const current = getChildScoreValue(child)
    if (current > 0) return current

    const fromName = parseScoreFromLabel(child.ChildName || child.ChildLabel)
    if (fromName !== null && fromName >= 0) return fromName

    return 0
}

const preserveUnselectedParent = parent => {
    const children = Array.isArray(parent.Children) ? parent.Children : []
    return {
        ...parent,
        IsSelected: false,
        Score: resolveParentDefaultScore(parent),
        Children: children.map(child => preserveUnselectedChild(child)),
    }
}

const preserveUnselectedChild = child => ({
    ...child,
    IsSelected: false,
    Score: resolveChildDefaultScore(child),
    IsScore: resolveChildIsScore(child),
})

const getManualScorePayloadValue = score => {
    const scoreNum = Number(score)
    return !Number.isNaN(scoreNum) && scoreNum >= 0 ? score : ''
}

const stripParentOtherFields = parent => {
    if (!parent) return parent
    const {
        IsOtherAnswerSelected: _isOther,
        OtherAnswer: _otherAnswer,
        OtherScore: _otherScore,
        ...rest
    } = parent
    return rest
}

export const getOtherAnswerForParent = (parent, otherAnswerResponse) => {
    if (!parent || !Array.isArray(otherAnswerResponse)) return null
    const uniqueId = parent.Id ?? parent.ParentId
    return (
        otherAnswerResponse.find(
            other =>
                String(other?.UniqueId) === String(uniqueId) ||
                String(other?.ParentId) === String(parent?.ParentId),
        ) || null
    )
}

const isParentOtherSelectedInAnswers = (parent, otherAnswerResponse) =>
    !!getOtherAnswerForParent(parent, otherAnswerResponse)

const getParentOtherScoreFromAnswers = (parent, otherAnswerResponse) => {
    const other = getOtherAnswerForParent(parent, otherAnswerResponse)
    if (!other) return 0

    if (parent?.AllowManualScore) {
        if (
            other.Score === '' ||
            other.Score === null ||
            other.Score === undefined
        ) {
            return 0
        }
        const score = Number(other.Score)
        return Number.isNaN(score) ? 0 : score
    }

    if (other.IsScore === false) return 0
    return resolveParentDefaultScore(parent)
}

const getParentScoreWithoutChildManualScore = parent => {
    const originalScore = Number(parent?.OriginalScore)
    if (!Number.isNaN(originalScore) && originalScore >= 0) {
        return parent.OriginalScore
    }

    const scoreFromName = parseScoreFromLabel(parent?.ParentName)
    if (scoreFromName !== null && scoreFromName >= 0) {
        return scoreFromName
    }

    return 0
}

export const applyParentChildResponseScores = parentChildResponse => {
    if (!Array.isArray(parentChildResponse)) return parentChildResponse

    return parentChildResponse.map(parent => {
        const children = Array.isArray(parent.Children) ? parent.Children : []
        const selectedChildren = children.filter(child => child?.IsSelected)
        const isParentSelected = !!parent?.IsSelected

        if (!isParentSelected && selectedChildren.length === 0) {
            return stripParentOtherFields(preserveUnselectedParent(parent))
        }

        const nextChildren = children.map(child => {
            if (!child?.IsSelected) {
                return preserveUnselectedChild(child)
            }

            if (parent.AllowManualScore) {
                return {
                    ...child,
                    Score: getManualScorePayloadValue(child.Score),
                    IsScore: true,
                }
            }

            return {
                ...child,
                Score: resolveChildDefaultScore(child),
                IsScore: resolveChildIsScore(child),
            }
        })

        const hasSelectedChild = nextChildren.some(child => child.IsSelected)

        if (hasSelectedChild) {
            const selectedChildScore = nextChildren.reduce((max, child) => {
                if (!child?.IsSelected) return max
                const scoreNum = resolveChildDefaultScore(child)
                return scoreNum > max ? scoreNum : max
            }, 0)

            let parentScore = resolveParentDefaultScore(parent)
            if (parent.AllowManualScore) {
                parentScore = getParentScoreWithoutChildManualScore(parent)
            } else if (selectedChildScore > 0) {
                parentScore = selectedChildScore
            }

            return stripParentOtherFields({
                ...parent,
                IsSelected: true,
                Score: parentScore,
                Children: nextChildren,
            })
        }

        if (parent.AllowManualScore) {
            return stripParentOtherFields({
                ...parent,
                IsSelected: true,
                Score: getManualScorePayloadValue(parent.Score),
                IsScore: true,
                Children: nextChildren,
            })
        }

        return stripParentOtherFields({
            ...parent,
            IsSelected: true,
            Score: resolveParentDefaultScore(parent),
            IsScore: parent.IsScore !== false,
            Children: nextChildren,
        })
    })
}

export const normalizeParentChildResponseAnswers = parentChildResponse => {
    if (!Array.isArray(parentChildResponse)) return parentChildResponse
    return parentChildResponse.map(parent => {
        const defaultParentScore = resolveParentDefaultScore(parent)
        return {
            ...parent,
            IsMultipleAllow: !!parent.IsMultipleAllow,
            OriginalScore:
                parent.OriginalScore ??
                (defaultParentScore > 0 ? defaultParentScore : undefined),
            Children: Array.isArray(parent.Children)
                ? parent.Children.map(child => {
                    const defaultChildScore = resolveChildDefaultScore(child)
                    return {
                        ...child,
                        IsScore: resolveChildIsScore(child),
                        OriginalScore:
                            child.OriginalScore ??
                            (defaultChildScore >= 0
                                ? defaultChildScore
                                : undefined),
                        Score:
                            child.Score !== undefined
                                ? child.Score
                                : child.ChildScore ?? 0,
                    }
                })
                : parent.Children,
        }
    })
}

export const isChildContributingScore = child => {
    if (!child?.IsSelected) return false
    if (child.IsScore === false) return false
    const scoreNum = getChildScoreValue(child)
    if (scoreNum > 0) return true
    return child.IsScore === true
}

export const getManualScoreUsedBySiblings = (
    parent,
    excludeChildId = null,
    otherAnswerResponse = null,
    excludeOther = false,
) => {
    let used = 0

    if (parent && Array.isArray(parent.Children)) {
        used = getSelectedChildren(parent).reduce((sum, child) => {
            if (excludeChildId != null && child.Id === excludeChildId) {
                return sum
            }
            const scoreNum = Number(child.Score)
            if (Number.isNaN(scoreNum) || scoreNum <= 0) return sum
            return sum + scoreNum
        }, 0)
    }

    if (
        !excludeOther &&
        isParentOtherSelectedInAnswers(parent, otherAnswerResponse)
    ) {
        used += getParentOtherScoreFromAnswers(parent, otherAnswerResponse)
    }

    return used
}

export const getRemainingManualScore = (
    parent,
    excludeChildId = null,
    otherAnswerResponse = null,
    excludeOther = false,
) => {
    const maxTotal = Number(parent?.MaxManualScore) || 0
    if (maxTotal <= 0) return 0
    const used = getManualScoreUsedBySiblings(
        parent,
        excludeChildId,
        otherAnswerResponse,
        excludeOther,
    )
    return Math.max(0, maxTotal - used)
}

export const clampManualScoreValue = (rawValue, maxAllowed) => {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
        return ''
    }
    const parsed = Number(rawValue)
    if (Number.isNaN(parsed) || parsed < 0) return 0
    if (maxAllowed > 0) {
        return Math.min(parsed, maxAllowed)
    }
    return parsed
}

const sumMultiAllowParentScore = (parent, otherAnswerResponse) => {
    if (!parent?.IsMultipleAllow) return 0

    if (parent.AllowManualScore) {
        const childTotal = getSelectedChildren(parent).reduce((sum, child) => {
            const scoreNum = Number(child.Score)
            if (Number.isNaN(scoreNum) || scoreNum <= 0) return sum
            return sum + scoreNum
        }, 0)

        const otherScore = isParentOtherSelectedInAnswers(parent, otherAnswerResponse)
            ? getParentOtherScoreFromAnswers(parent, otherAnswerResponse)
            : 0
        return childTotal + otherScore
    }

    let total = 0
    getSelectedChildren(parent).forEach(child => {
        if (isChildContributingScore(child)) {
            total += getChildScoreValue(child)
        }
    })

    if (
        isParentOtherSelectedInAnswers(parent, otherAnswerResponse) &&
        parent.IsScore !== false
    ) {
        const parentScore = getParentOtherScoreFromAnswers(
            parent,
            otherAnswerResponse,
        )
        if (parentScore > 0) {
            total += parentScore
        }
    }

    const children = Array.isArray(parent.Children) ? parent.Children : []
    if (
        getSelectedChildren(parent).length === 0 &&
        !!parent.IsSelected &&
        children.length === 0 &&
        parent.IsScore
    ) {
        const parentScore = resolveParentDefaultScore(parent)
        if (parentScore > 0) {
            total += parentScore
        }
    }

    return total
}

const getSingleSelectParentScore = (
    selectedParent,
    fallbackQuestionScore,
    otherAnswerResponse,
) => {
    if (!selectedParent || selectedParent.IsMultipleAllow) return 0

    const children = Array.isArray(selectedParent.Children)
        ? selectedParent.Children
        : []
    const selectedChildren = getSelectedChildren(selectedParent)
    const selectedChild =
        getLastSelectedChild(selectedParent) ||
        selectedChildren[0] ||
        children.find(c => !!c.IsSelected)

    if (selectedParent.AllowManualScore) {
        if (isParentOtherSelectedInAnswers(selectedParent, otherAnswerResponse)) {
            const otherScore = getParentOtherScoreFromAnswers(
                selectedParent,
                otherAnswerResponse,
            )
            if (otherScore > 0) {
                return otherScore
            }
            if (selectedParent.IsScore) {
                return otherScore
            }
            return 0
        }

        if (children.length > 0 && selectedChild) {
            const childScore = Number(selectedChild.Score)
            if (!Number.isNaN(childScore) && childScore > 0) {
                return childScore
            }
            if (selectedChild.IsScore) {
                return Number.isNaN(childScore) ? 0 : childScore
            }
            return 0
        }

        const parentScore = Number(selectedParent.Score)
        if (!Number.isNaN(parentScore) && parentScore > 0) {
            return parentScore
        }
        if (selectedParent.IsScore) {
            return Number.isNaN(parentScore) ? 0 : parentScore
        }
        return 0
    }

    if (
        isParentOtherSelectedInAnswers(selectedParent, otherAnswerResponse) &&
        selectedParent.IsScore !== false
    ) {
        const parentScore = getParentOtherScoreFromAnswers(
            selectedParent,
            otherAnswerResponse,
        )
        if (parentScore > 0) return parentScore
    }

    if (children.length > 0 && selectedChildren.length > 0) {
        const childScore = selectedChildren.reduce((max, child) => {
            if (!isChildContributingScore(child)) return max
            const scoreNum = getChildScoreValue(child)
            return scoreNum > max ? scoreNum : max
        }, 0)
        if (childScore > 0) return childScore
    }

    if (selectedChild && isChildContributingScore(selectedChild)) {
        const scoreNum = getChildScoreValue(selectedChild)
        if (scoreNum > 0) return scoreNum
    }

    if (selectedChild && selectedParent.IsScore !== false) {
        const parentScore = resolveParentDefaultScore(selectedParent)
        if (parentScore > 0) return parentScore
    }

    if (
        selectedChildren.length === 0 &&
        !!selectedParent.IsSelected &&
        children.length === 0 &&
        selectedParent.IsScore
    ) {
        const scoreNum = resolveParentDefaultScore(selectedParent)
        if (scoreNum > 0) {
            return scoreNum
        }
    }

    if (
        selectedChildren.length === 1 &&
        children.length === 1 &&
        isChildContributingScore(selectedChildren[0])
    ) {
        const scoreNum = getChildScoreValue(selectedChildren[0])
        if (scoreNum > 0) return scoreNum
    }

    if (
        selectedChild &&
        isChildContributingScore(selectedChild) &&
        typeof fallbackQuestionScore === 'number' &&
        fallbackQuestionScore > 0
    ) {
        return fallbackQuestionScore
    }

    return 0
}

/**
 * Resolves the question score from ParentChildResponse answers.
 * Multi-allow parents: sum every selected child's score across all such parents.
 * Single-select parents: last selected child wins (unchanged legacy behavior).
 */
export const getParentChildQuestionScore = (
    parentChildResponse,
    fallbackQuestionScore = 0,
    otherAnswerResponse = null,
) => {
    if (!Array.isArray(parentChildResponse) || parentChildResponse.length === 0) {
        return 0
    }

    const hasMultiAllowSelection = parentChildResponse.some(parent => {
        if (!parent?.IsMultipleAllow) return false
        if (getSelectedChildren(parent).length > 0) return true
        if (isParentOtherSelectedInAnswers(parent, otherAnswerResponse)) {
            return true
        }
        return (
            !!parent.IsSelected &&
            (!Array.isArray(parent.Children) || parent.Children.length === 0)
        )
    })

    if (hasMultiAllowSelection) {
        const total = parentChildResponse.reduce(
            (sum, parent) =>
                sum + sumMultiAllowParentScore(parent, otherAnswerResponse),
            0,
        )
        if (total > 0) return total

        const selectedParent = getSelectedParentFromPcr(parentChildResponse)
        if (selectedParent) {
            const defaultScore = resolveParentDefaultScore(selectedParent)
            if (defaultScore > 0) return defaultScore
        }
        return 0
    }

    const selectedParent = getSelectedParentFromPcr(parentChildResponse)
    if (!selectedParent) return 0

    return getSingleSelectParentScore(
        selectedParent,
        fallbackQuestionScore,
        otherAnswerResponse,
    )
}
